/**
 * Push-to-talk and spoken answers (scope §14). Hold the big button (or Space
 * while the agent dock is focused) to talk. Short utterances use the browser's
 * Web Speech API when available; dictation (> ~10 s) or browsers without it
 * record audio and send it to the server's Whisper backend. Transcripts are
 * shown for correction in dictation mode and sent immediately in command mode.
 * Speech out is off by default and toggled per session; only the agent's
 * "Spoken:" line is read, never the whole answer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mic, Volume2, VolumeX } from 'lucide-react';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';

type SR = { start(): void; stop(): void; abort(): void; onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>>; resultIndex: number }) => void) | null; onerror: ((e: { error: string }) => void) | null; onend: (() => void) | null; continuous: boolean; interimResults: boolean; lang: string };
const SRCtor = (): (new () => SR) | null => (window as unknown as { SpeechRecognition?: new () => SR; webkitSpeechRecognition?: new () => SR }).SpeechRecognition ?? (window as unknown as { webkitSpeechRecognition?: new () => SR }).webkitSpeechRecognition ?? null;

export const SPEAK_KEY = 'cortex.voice.speak';
const DICTATION_MS = 10_000;

export function VoiceControls({ onTranscript, mode, onMode, speak, onSpeak }: { onTranscript: (text: string, final: boolean) => void; mode: 'command' | 'dictation'; onMode: (m: 'command' | 'dictation') => void; speak: boolean; onSpeak: (b: boolean) => void }) {
  const cfg = useQuery({ queryKey: ['voice-config'], queryFn: Api.voice.config, staleTime: 300_000 });
  const toast = useUi((s) => s.toast);
  const [listening, setListening] = useState(false);
  const [level, setLevel] = useState<'web' | 'whisper' | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const sr = useRef<SR | null>(null);
  const startedAt = useRef(0);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);
  const useWhisper = mode === 'dictation' ? !!cfg.data?.stt : !SRCtor() && !!cfg.data?.stt;

  const stop = useCallback(() => {
    if (!listening) return;
    setListening(false);
    if (timer.current) window.clearTimeout(timer.current);
    if (sr.current) {
      try {
        sr.current.stop();
      } catch {
        /* already stopped */
      }
    }
    if (rec.current && rec.current.state !== 'inactive') rec.current.stop();
  }, [listening]);

  const start = useCallback(async () => {
    if (listening) return;
    startedAt.current = Date.now();
    if (!useWhisper) {
      const C = SRCtor();
      if (!C) return toast('No speech recognition available in this browser and no server STT configured', 'warn');
      const r = new C();
      r.lang = 'en-AU';
      r.continuous = true;
      r.interimResults = true;
      let finalText = '';
      r.onresult = (e) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if ((e.results[i] as unknown as { isFinal?: boolean }).isFinal) finalText += t + ' ';
          else interim += t;
        }
        onTranscript((finalText + interim).trim(), false);
      };
      r.onerror = (e) => e.error !== 'aborted' && toast(`speech: ${e.error}`, 'warn');
      r.onend = () => {
        setListening(false);
        setLevel(null);
        onTranscript(finalText.trim(), true);
      };
      sr.current = r;
      r.start();
      setLevel('web');
      setListening(true);
      // Web Speech accuracy collapses past ~10 s; hand long takes to Whisper next time.
      timer.current = window.setTimeout(() => cfg.data?.stt && toast('Long dictation: switch to Dictation mode for the Whisper engine', 'info'), DICTATION_MS);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        setLevel(null);
        const blob = new Blob(chunks.current, { type: mime });
        if (blob.size < 2000) return;
        onTranscript('… transcribing', false);
        try {
          const t = await Api.voice.transcribe(blob);
          onTranscript(t.text, true);
        } catch (err) {
          toast((err as Error).message, 'crit');
          onTranscript('', true);
        }
      };
      mr.start(250);
      rec.current = mr;
      setLevel('whisper');
      setListening(true);
    } catch (err) {
      toast(`microphone: ${(err as Error).message}`, 'crit');
    }
  }, [listening, useWhisper, cfg.data?.stt, onTranscript, toast]);

  // Hold Space while the dock has focus (not in a text field).
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, .cm-editor, [contenteditable]')) return;
      if (!t?.closest('.agent')) return;
      e.preventDefault();
      void start();
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space' && listening) (e.preventDefault(), stop());
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => (window.removeEventListener('keydown', down), window.removeEventListener('keyup', up));
  }, [start, stop, listening]);

  return (
    <div className="voice-controls">
      <button
        className={`ptt${listening ? ' on' : ''}`}
        title={`Hold to talk (${useWhisper ? 'Whisper' : 'browser speech'}); or hold Space with the dock focused`}
        onMouseDown={() => void start()}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={(e) => (e.preventDefault(), void start())}
        onTouchEnd={stop}
      >
        <Mic size={18} /> {listening ? `listening (${level})` : 'hold to talk'}
      </button>
      <select value={mode} onChange={(e) => onMode(e.target.value as 'command')} title="Command: send as soon as you let go. Dictation: show the transcript for correction first (Whisper engine when configured).">
        <option value="command">command</option>
        <option value="dictation">dictation</option>
      </select>
      <button className={`icon${speak ? ' on' : ''}`} title={speak ? 'Spoken answers on (only the Spoken: line is read)' : 'Spoken answers off'} onClick={() => onSpeak(!speak)}>{speak ? <Volume2 size={14} /> : <VolumeX size={14} />}</button>
    </div>
  );
}

/** Speak a line: server TTS (Piper/ElevenLabs) when configured, else the browser. */
export async function speakLine(text: string, serverTts: boolean): Promise<void> {
  if (!text) return;
  if (serverTts) {
    try {
      const blob = await Api.voice.speak(text);
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      a.onended = () => URL.revokeObjectURL(url);
      await a.play();
      return;
    } catch {
      /* fall through to the browser voice */
    }
  }
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-AU';
    u.rate = 1.0;
    window.speechSynthesis.speak(u);
  }
}

/** Pull the agent's spoken line out of an answer. */
export function spokenLine(text: string): string | null {
  const m = /^Spoken:\s*(.+)$/m.exec(text);
  return m ? m[1].trim() : null;
}
